import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import HamburgerIcon from "../UI/atoms/HamburgerIcon/HamburgerIcon";
import Image from "../UI/atoms/Image";
import LanguageSelector from "../UI/molecules/LanguageSelector/LanguageSelector";
import Footer from "../UI/organisms/Footer";
import MobileMenuDrawer from "../UI/organisms/MobileMenuDrawer";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const [isMouseNearTop, setIsMouseNearTop] = React.useState(false);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const { i18n, t } = useTranslation();

  const languages = [
    { code: "es", label: "ES" },
    { code: "en", label: "EN" },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    document.documentElement.lang = lng;
  };

  // Sync lang attribute on initial load
  useEffect(() => {
    document.documentElement.lang = i18n.language?.split("-")[0] || "en";
  }, [i18n.language]);

  const toggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Detectar scroll y ocultar header cuando se detiene
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      // Actualizar si ha hecho scroll
      setIsScrolled(scrollY > 50);

      // Mostrar header mientras se hace scroll
      setIsScrolling(true);

      // Limpiar timeout anterior
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Ocultar header después de 2 segundos sin scroll
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 500);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Detectar cuando el cursor está cerca del top
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Si el cursor está a menos de 100px del top, mostrar el header
      setIsMouseNearTop(e.clientY < 100);
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // Prevenir scroll cuando el menú está abierto
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Skip to main content — visible only on keyboard focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:text-blue-600 focus:px-4 focus:py-2 focus:rounded focus:shadow-lg focus:font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
      >
        Skip to main content
      </a>
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 w-full flex items-center justify-between px-6 py-6 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white/95 backdrop-blur-sm shadow-sm" : "bg-transparent"
        } ${!isScrolling && isScrolled && !isMouseNearTop ? "-translate-y-full" : "translate-y-0"}`}
      >
        <div className="flex items-center space-x-8">
          <div className="flex items-center space-x-2">
            <Link to="/" className="h-8 w-32 flex items-center">
              <Image src="/logos/horizontal/sv-h-blue.png" alt="Surevision" className="h-full w-auto object-contain" width="128" height="32" />
            </Link>
          </div>

          <div className="hidden md:flex space-x-8">
            <Link to="/team" className="text-blue-600 font-medium hover:text-blue-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded">
              {t("layout.aboutUs")}
            </Link>
            <Link to="/contact" className="text-blue-600 font-medium hover:text-blue-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded">
              {t("layout.contact")}
            </Link>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="hidden md:block">
            <LanguageSelector
              languages={languages}
              currentLanguage={i18n.language}
              onLanguageChange={changeLanguage}
              variant="desktop"
            />
          </div>

          <button
            onClick={toggleMenu}
            className="md:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
            aria-label={t("layout.openMenu")}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <HamburgerIcon isOpen={isMobileMenuOpen} />
          </button>
        </div>
      </nav>

      <MobileMenuDrawer
        isOpen={isMobileMenuOpen}
        onClose={closeMenu}
        logoSrc="/logos/horizontal/sv-h-blue.png"
        currentLanguage={i18n.language}
        onLanguageChange={changeLanguage}
      />

      <main id="main-content">{children}</main>

      <Footer />
    </div>
  );
};

export default Layout;
